import { useEffect, useState } from "react";
import { FiEye } from "react-icons/fi";
import { FaCarAlt } from "react-icons/fa";
import {
  ChevronRight,
  Phone,
  Calendar,
  Gauge,
  Fuel,
  Instagram,
  MapPinned,
  ArrowUpRight,
} from "lucide-react";
import Button from "../components/ui/Button";
import {
  getFirestore,
  collection,
  doc,
  query,
  where,
  orderBy,
  onSnapshot,
  getDoc,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import app from "../firebase-config";
import { id_firebase } from "../content-site";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { formatMoney } from "../utils/formatMoney";
import StatsSection from "../components/StatsSection";
import FeaturesSection from "../components/FeaturesSection";
import CTASection from "../components/CTASection";
import { retiraMascara } from "../utils/retiraMascara";

const firestore = getFirestore(app);
const lojaMapEmbedUrl = "";

export default function Home() {
  const [veiculos, setVeiculos] = useState([]);
  const [revenda, setRevenda] = useState({});
  const [filtros, setFiltros] = useState({ marca: "", modelo: "", ano: "" });
  const [filtrosAvancados, setFiltrosAvancados] = useState({
    tipo: "",
    cor: "",
    portas: "",
    kmMax: "",
    precoMin: "",
    precoMax: "",
  });
  const [, setErroFiltro] = useState("");

  const navigate = useNavigate();
  const [telefone, setTelefone] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);

    const revendaRef = doc(collection(firestore, "revenda"), id_firebase);
    getDoc(revendaRef).then((docSnap) => {
      if (docSnap.exists()) {
        setRevenda(docSnap.data());
        setTelefone(retiraMascara(docSnap.data().petelefone2, "link"));
      }
    });

    const q = query(
      collection(
        doc(collection(firestore, "revenda"), id_firebase),
        "veiculos",
      ),
      where("veremovido", "==", 0),
      where("vevendido", "==", 0),
      orderBy("updated_at", "desc"),
    );

    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const items = [];
      querySnapshot.forEach((doc) => {
        const document = doc.data();
        document.id = doc.id;
        if (document["veexibir_site"] !== false) {
          items.push(document);
        }
      });
      setVeiculos(items);
    });

    return () => unsubscribe();
  }, []);

  function getOpcoesFiltro(campo, marcaSelecionada = "") {
    const modelosFiltrados = veiculos.filter((veiculo) => {
      if (campo === "vefipe_name" && marcaSelecionada) {
        return veiculo.vefipe_marca === marcaSelecionada;
      }
      return true;
    });

    const opcoes = modelosFiltrados
      .map((veiculo) => veiculo[campo])
      .filter((valor, index, self) => valor && self.indexOf(valor) === index);

    return opcoes.sort();
  }

  const handleBuscar = () => {
    if (
      !filtros.marca &&
      !filtros.modelo &&
      !filtros.ano &&
      !Object.values(filtrosAvancados).some((value) => value)
    ) {
      setErroFiltro("Selecione pelo menos um filtro.");
      return;
    }

    const params = new URLSearchParams();
    // Mapeia os nomes dos filtros para os nomes dos campos do veículo
    if (filtros.marca) params.append("vefipe_marca", filtros.marca);
    if (filtros.modelo) params.append("vefipe_name", filtros.modelo);
    if (filtros.ano) params.append("vefipe_ano_modelo", filtros.ano);

    Object.entries(filtrosAvancados).forEach(([key, value]) => {
      if (value) {
        params.append(key, value);
      }
    });

    setErroFiltro("");
    navigate(`/estoque?${params.toString()}`);
  };

  function separarNomeVeiculo(nomeCompleto) {
    if (!nomeCompleto) return { mainName: "", subName: "" };

    const palavras = nomeCompleto.split(" ");

    const STOP_WORDS = [
      "CD",
      "SR",
      "SRV",
      "GR",
      "XEI",
      "LTZ",
      "LX",
      "4X4",
      "4X2",
      "1.0",
      "1.2",
      "1.3",
      "1.4",
      "1.5",
      "1.8",
      "1.6",
      "2.0",
      "2.8",
      "TDI",
      "TURBO",
      "DIESEL",
      "FLEX",
      "AUT",
      "AUT.",
      "MT",
      "CVT",
    ];

    const indexSpec = palavras.findIndex((p) => STOP_WORDS.includes(p));

    if (indexSpec === -1) {
      return {
        mainName: palavras.slice(0, 1).join(" "),
        subName: palavras.slice(1).join(" "),
      };
    }

    return {
      mainName: palavras.slice(0, indexSpec).join(" "),
      subName: palavras.slice(indexSpec).join(" "),
    };
  }

  return (
    <div className="min-h-screen w-full bg-background">
      <Header />

      <section className="relative min-h-screen flex items-center justify-center overflow-hidden py-20 lg:py-0">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="./heroTruck.png"
            alt="Carros poderosos"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 overlay-gradient" />
          <div className="absolute inset-0 bg-background/60" />
        </div>

        {/* Decorative Elements */}
        <div className="absolute top-0 left-0 w-full h-1 accent-gradient" />
        <div className="absolute bottom-0 right-0 w-1/3 h-1 accent-gradient" />

        {/* Angular accent shape */}
        <div className="absolute -left-20 top-1/4 w-40 h-40 border-2 border-primary/20 rotate-45 animate-pulse-slow" />
        <div className="absolute -right-10 bottom-1/3 w-32 h-32 border border-primary/10 rotate-12" />

        {/* Content */}
        <div className="relative z-10 container mx-auto px-6 mt-32 ">
          <div className="grid items-center gap-12 lg:grid-cols-[1.05fr_0.95fr]">
            <div className="text-center lg:text-left">
              <div className="animate-slide-up">
                <span className="inline-flex items-center gap-2 px-4 py-2 mb-6 text-sm font-semibold tracking-widest uppercase border border-primary/50 text-primary rounded-full bg-background/40 backdrop-blur-sm">
                  <span className="h-2 w-2 rounded-full bg-primary animate-pulse" />
                  Seu carro dos sonhos está aqui
                </span>
              </div>

              <h1
                className="text-display text-5xl md:text-7xl lg:text-8xl font-bold mb-6 leading-none animate-slide-up"
                style={{ animationDelay: "0.1s" }}
              >
                Qualidade que <span className="text-primary">inspira</span>
                <br />a sua vida
              </h1>

              <p
                className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto lg:mx-0 mb-10 animate-slide-up"
                style={{ animationDelay: "0.2s" }}
              >
                Carros novos e seminovos com as melhores condições do mercado.
                Qualidade, potência e confiança em cada veículo.
              </p>

              <div
                className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start animate-slide-up"
                style={{ animationDelay: "0.3s" }}
              >
                <Button variant="hero" size="xl" href="/estoque">
                  Ver Estoque
                  <ChevronRight className="h-5 w-5" />
                </Button>
                <Button variant="heroOutline" size="xl" href="/contato">
                  <Phone className="h-5 w-5" />
                  Fale Conosco
                </Button>
              </div>
            </div>

            <div
              className="grid gap-4 animate-slide-up"
              style={{ animationDelay: "0.35s" }}
            >
              <a
                href={revenda.instagram_link || "https://www.instagram.com/"}
                target="_blank"
                rel="noreferrer"
                className="group relative overflow-hidden rounded-3xl border border-primary/30 bg-background/70 backdrop-blur-md p-6 shadow-2xl shadow-black/30 transition-all duration-300 hover:-translate-y-1 hover:border-primary/60"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-transparent to-transparent opacity-70" />
                <div className="relative flex items-start justify-between gap-4">
                  <div>
                    <span className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.25em] text-primary">
                      Instagram
                    </span>
                    <h2 className="mt-4 text-display text-3xl md:text-4xl font-bold text-foreground">
                      Acompanhe as novidades da loja em tempo real
                    </h2>
                    <p className="mt-3 max-w-md text-sm md:text-base text-muted-foreground">
                      Veja lançamentos, bastidores, ofertas e veículos
                      recém-chegados.
                    </p>
                  </div>
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-primary text-primary-foreground shadow-lg shadow-primary/30 transition-transform duration-300 group-hover:scale-105">
                    <Instagram className="h-7 w-7" />
                  </div>
                </div>

                <div className="relative mt-6 flex items-center justify-between rounded-2xl border border-primary/20 bg-card/80 px-4 py-4">
                  <div>
                    <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">
                      Maverick Motors II
                    </p>
                    <p className="mt-1 text-base font-semibold text-foreground">
                      Abrir perfil da loja
                    </p>
                  </div>
                  <div className="flex h-11 w-11 items-center justify-center rounded-full bg-primary/15 text-primary transition-transform duration-300 group-hover:translate-x-1">
                    <ArrowUpRight className="h-5 w-5" />
                  </div>
                </div>
              </a>

              <div className="relative overflow-hidden rounded-3xl border border-primary/25 bg-background/70 backdrop-blur-md p-4 shadow-2xl shadow-black/30">
                <div className="mb-4 flex items-center justify-between gap-4 px-2">
                  <div>
                    <span className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.25em] text-primary">
                      Localização
                    </span>
                    <h2 className="mt-3 text-display text-2xl md:text-3xl font-bold text-foreground">
                      Mapa da loja
                    </h2>
                  </div>
                  <MapPinned className="h-8 w-8 text-primary" />
                </div>

                <div className="overflow-hidden rounded-2xl border border-border/60 bg-card">
                  {lojaMapEmbedUrl ? (
                    <iframe
                      title="Mapa da loja"
                      src={lojaMapEmbedUrl}
                      className="h-[280px] w-full"
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    />
                  ) : (
                    <div className="flex h-[280px] w-full flex-col items-center justify-center gap-3 bg-gradient-to-br from-card via-background to-card px-6 text-center">
                      <iframe
                        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1826.3468185227814!2d-46.54888564310571!3d-23.722632089076676!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x94ce419bd4d45053%3A0x8100463ee4f1ec90!2sR.%20Mal.%20Deodoro%2C%202785%20-%20Centro%2C%20S%C3%A3o%20Bernardo%20do%20Campo%20-%20SP%2C%2009710-193!5e0!3m2!1spt-BR!2sbr!4v1787011293794!5m2!1spt-BR!2sbr"
                        className="w-full h-full"
                      ></iframe>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-float">
          <div className="w-6 h-10 border-2 border-foreground/30 rounded-full flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-primary rounded-full animate-pulse" />
          </div>
        </div>
      </section>

      <StatsSection />

      <section className="py-24 bg-background">
        <div className="container mx-auto px-6">

            <div className="text-center mb-16">
              <span className="inline-block px-4 py-2 mb-4 text-sm font-semibold tracking-widest uppercase text-primary border border-primary/30 rounded-full">
                Estoque
              </span>
              <h2 className="text-display text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-4">
                Carros em <span className="text-primary">Destaque</span>
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Confira nossa seleção especial de carros prontos para rodar.
                Todos revisados e com garantia.
              </p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-7xl mx-auto px-4 mt-10">
              {veiculos
                .slice()
                .sort(() => Math.random() - 0.5)
                .slice(0, 3)
                .map((veiculo) => {
                  const { mainName, subName } = separarNomeVeiculo(
                    veiculo.vefipe_name,
                  );

                  const cambio =
                    veiculo.vecambio === "A"
                      ? "Automático"
                      : veiculo.vecambio === "M"
                        ? "Manual"
                        : veiculo.vecambio;

                  veiculo.vecambio = cambio;

                  return (
                    <div
                      key={veiculo.id}
                      className="group relative bg-card rounded-lg overflow-hidden card-shadow border border-border/50 hover:border-primary/50 transition-all duration-500 hover:-translate-y-2 md:min-w-[280px]"
                    >
                      <div className="relative h-70 overflow-hidden bg-black/5 flex items-center justify-center">
                        <img
                          className="w-full h-full object-cover"
                          src={veiculo.imagens[0]?.fileUrl}
                          alt={veiculo.titulo}
                        />
                        <div className="absolute inset-0" />
                        <div className="absolute inset-0 bg-primary/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                      </div>

                      <div className="p-6">
                        <div>
                          <h3 className="text-display text-2xl font-bold text-foreground group-hover:text-primary transition-colors">
                            {veiculo.vefipe_marca} {mainName}
                          </h3>

                          {subName && (
                            <p className="text-sm text-muted-foreground uppercase mb-4">
                              {subName}
                            </p>
                          )}
                        </div>

                        <div className="grid grid-cols-2 gap-4 mb-6">
                          <div className="flex flex-col items-center p-3 bg-secondary rounded-lg">
                            <Calendar className="w-4 h-4 text-primary mb-1" />
                            <span className="text-xs text-muted-foreground">
                              Ano Modelo
                            </span>
                            <span className="text-sm font-semibold text-foreground">
                              {veiculo.vefipe_ano_modelo}
                            </span>
                          </div>
                          <div className="flex flex-col items-center p-3 bg-secondary rounded-lg">
                            <Calendar className="w-4 h-4 text-primary mb-1" />
                            <span className="text-xs text-muted-foreground">
                              Ano Fabricação
                            </span>
                            <span className="text-sm font-semibold text-foreground">
                              {veiculo.veano_fabricacao}
                            </span>
                          </div>
                          <div className="flex flex-col items-center p-3 bg-secondary rounded-lg">
                            <Gauge className="w-4 h-4 text-primary mb-1" />
                            <span className="text-xs text-muted-foreground">
                              KM
                            </span>
                            <span className="text-sm font-semibold text-foreground">
                              {veiculo.vequilometragem}
                            </span>
                          </div>
                          <div className="flex flex-col items-center p-3 bg-secondary rounded-lg">
                            <Fuel className="w-4 h-4 text-primary mb-1" />
                            <span className="text-xs text-muted-foreground">
                              Combustível
                            </span>
                            <span className="text-sm font-semibold text-foreground">
                              {veiculo.vefipe_combustivel}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center justify-between">
                          <div>
                            <span className="text-xs text-muted-foreground">
                              A partir de
                            </span>
                            <p className="text-display text-2xl font-bold text-primary">
                              {" "}
                              {veiculo.vevalor
                                ? formatMoney(veiculo.vevalor)
                                : "Consulte o preço"}
                            </p>
                          </div>

                          <Button
                            href={`/veiculo/${veiculo.id}`}
                            variant="outline"
                            size="sm"
                          >
                            <FiEye className="w-4 h-4 ml-2 group-hover/btn:translate-x-1 transition-transform" />{" "}
                            Ver Detalhes
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
        </div>
      </section>

      <div className="text-center mt-10 mb-20">
        <Button
          variant="hero"
          size="lg"
          className="bg-primary text-primary-foreground hover:bg-primary/90 px-6 py-3 rounded-md text-lg font-semibold inline-flex gap-2 items-center"
          href="/estoque"
        >
          <FaCarAlt /> Ver todo o Estoque
        </Button>
      </div>

      <FeaturesSection />
      <CTASection />

      <Footer />
    </div>
  );
}
